"use client";

import { useEffect, useState } from "react";

type LenisLike = {
  on: (event: "scroll", callback: () => void) => void;
  off: (event: "scroll", callback: () => void) => void;
  scrollTo: (target: number, options?: { duration?: number }) => void;
};

declare global {
  interface Window {
    __lenis?: LenisLike;
  }
}

// A back-to-top button that appears once the visitor has scrolled past 60% of the
// page. Mounted globally (see layout.tsx) so it works on every page. Drives the
// Lenis smooth-scroll instance when present, otherwise falls back to the native
// smooth scroll. Hidden until needed and from the accessibility tree while hidden.
export default function ScrollToTop() {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const compute = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const frac = max > 0 ? window.scrollY / max : 0;
      document.documentElement.classList.toggle("iso-hidden", window.scrollY > 8);
      setShown(frac >= 0.6);
    };

    compute();
    // Lenis emits its own scroll events; fall back to the window listener too.
    const lenis = () => window.__lenis;
    let bound: (() => void) | undefined;
    const tryBindLenis = () => {
      const l = lenis();
      if (l && !bound) {
        bound = compute;
        l.on("scroll", compute);
      }
    };
    tryBindLenis();
    const poll = window.setInterval(tryBindLenis, 500);
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);

    return () => {
      window.clearInterval(poll);
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
      const l = lenis();
      if (l && bound) l.off("scroll", bound);
    };
  }, []);

  const toTop = () => {
    const l = window.__lenis;
    if (l && typeof l.scrollTo === "function") {
      l.scrollTo(0, { duration: 0.8 });
    } else {
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
    }
  };

  return (
    <button
      type="button"
      className={shown ? "scroll-top shown" : "scroll-top"}
      aria-label="Back to top"
      aria-hidden={!shown}
      tabIndex={shown ? 0 : -1}
      onClick={toTop}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 19V6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 11.5 12 5.5 18 11.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}
